import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Trash2, Plus, Image as ImageIcon, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { AdminVenue, VenueFormData } from '../../types/admin';

interface Props {
  venue?: AdminVenue | null;
  onClose: () => void;
  onSubmit: (data: VenueFormData) => Promise<void>;
}

export default function VenueFormModal({ venue, onClose, onSubmit }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<VenueFormData>({
    name: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    capacity_details: {
      max_capacity: 0,
      seated_capacity: 0,
      standing_capacity: 0
    },
    room_configurations: {
      indoor_spaces: [],
      outdoor_spaces: [],
      special_areas: []
    },
    amenities: [],
    facilities: [],
    pricing_structure: {
      base_price: 0,
      currency: 'LKR',
      includes: [],
      additional_fees: {}
    },
    accessibility_features: [],
    parking_info: '',
    catering_options: [],
    photos: [],
    floor_plans: []
  });

  const commonAmenities = [
    'Air Conditioning', 'Sound System', 'Lighting', 'Stage', 'Dance Floor',
    'Bridal Suite', 'Kitchen', 'Bar Area', 'Garden', 'Pool', 'Parking',
    'WiFi', 'Security', 'Restrooms', 'Storage'
  ];

  const commonFacilities = [
    'Reception Hall', 'Ceremony Space', 'Cocktail Area', 'Outdoor Terrace',
    'Private Dining Room', 'Conference Room', 'Changing Rooms', 'Photo Studio'
  ];

  const accessibilityFeatures = [
    'Wheelchair Accessible', 'Elevator Access', 'Accessible Restrooms',
    'Ramp Access', 'Wide Doorways', 'Accessible Parking', 'Sign Language Support'
  ];

  const cateringOptions = [
    'In-house Catering', 'External Catering Allowed', 'Full Kitchen',
    'Prep Kitchen Only', 'Bar Service', 'Kosher Kitchen', 'Halal Options'
  ];

  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue.name,
        address: venue.address,
        contact_email: venue.contact_email,
        contact_phone: venue.contact_phone,
        capacity_details: venue.capacity_details as any,
        room_configurations: venue.room_configurations as any,
        amenities: venue.amenities,
        facilities: venue.facilities,
        pricing_structure: venue.pricing_structure as any,
        accessibility_features: venue.accessibility_features,
        parking_info: venue.parking_info || '',
        catering_options: venue.catering_options,
        photos: venue.photos,
        floor_plans: venue.floor_plans
      });
    }
  }, [venue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting venue form:', err);
      setError('Failed to save venue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleArrayToggle = (array: string[], item: string, field: keyof VenueFormData) => {
    const newArray = array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
    
    setFormData(prev => ({
      ...prev,
      [field]: newArray
    }));
  };

  const addCustomItem = (field: keyof VenueFormData, arrayPath?: string) => {
    const item = prompt(`Enter new ${field}:`);
    if (item) {
      if (arrayPath) {
        setFormData(prev => ({
          ...prev,
          [field]: {
            ...prev[field],
            [arrayPath]: [...(prev[field] as any)[arrayPath], item]
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: [...(prev[field] as string[]), item]
        }));
      }
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingPhoto(true);
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `venues/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('vendor-venue-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('vendor-venue-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedUrls]
      }));
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError('Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
      <div className="card-pastel p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800 font-poppins">
            {venue ? 'Edit Venue' : 'Add New Venue'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Venue Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Contact Email *
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Contact Phone *
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Address *
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-pastel"
                  rows={2}
                  required
                />
              </div>
            </div>
          </div>

          {/* Capacity Details */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Capacity Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Maximum Capacity *
                </label>
                <input
                  type="number"
                  value={formData.capacity_details.max_capacity === 0 ? '' : formData.capacity_details.max_capacity}
                  onChange={(e) => setFormData({
                    ...formData,
                    capacity_details: {
                      ...formData.capacity_details,
                      max_capacity: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="input-pastel"
                  min="1"
                  placeholder="Enter maximum capacity"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Seated Capacity
                </label>
                <input
                  type="number"
                  value={formData.capacity_details.seated_capacity === 0 ? '' : formData.capacity_details.seated_capacity}
                  onChange={(e) => setFormData({
                    ...formData,
                    capacity_details: {
                      ...formData.capacity_details,
                      seated_capacity: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="input-pastel"
                  min="0"
                  placeholder="Enter seated capacity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Standing Capacity
                </label>
                <input
                  type="number"
                  value={formData.capacity_details.standing_capacity === 0 ? '' : formData.capacity_details.standing_capacity}
                  onChange={(e) => setFormData({
                    ...formData,
                    capacity_details: {
                      ...formData.capacity_details,
                      standing_capacity: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="input-pastel"
                  min="0"
                  placeholder="Enter standing capacity"
                />
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-800 font-poppins">Amenities</h4>
              <button
                type="button"
                onClick={() => addCustomItem('amenities')}
                className="btn-pastel flex items-center space-x-1 px-2 py-1 text-sm"
              >
                <Plus className="w-3 h-3" />
                <span>Custom</span>
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {commonAmenities.map(amenity => (
                <label key={amenity} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.amenities.includes(amenity)}
                    onChange={() => handleArrayToggle(formData.amenities, amenity, 'amenities')}
                    className="h-4 w-4 bg-white/60 border-pastel-purple-200 rounded text-pastel-purple-600 focus:ring-pastel-purple-400"
                  />
                  <span className="text-gray-600 text-sm">{amenity}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Facilities */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-800 font-poppins">Facilities</h4>
              <button
                type="button"
                onClick={() => addCustomItem('facilities')}
                className="btn-pastel flex items-center space-x-1 px-2 py-1 text-sm"
              >
                <Plus className="w-3 h-3" />
                <span>Custom</span>
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {commonFacilities.map(facility => (
                <label key={facility} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.facilities.includes(facility)}
                    onChange={() => handleArrayToggle(formData.facilities, facility, 'facilities')}
                    className="h-4 w-4 bg-white/60 border-pastel-purple-200 rounded text-pastel-purple-600 focus:ring-pastel-purple-400"
                  />
                  <span className="text-gray-600 text-sm">{facility}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Pricing Structure */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Pricing Structure</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Base Price
                </label>
                <input
                  type="number"
                  value={formData.pricing_structure.base_price === 0 ? '' : formData.pricing_structure.base_price}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      base_price: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="input-pastel"
                  min="0"
                  placeholder="Enter base price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Currency
                </label>
                <select
                  value={formData.pricing_structure.currency}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      currency: e.target.value
                    }
                  })}
                  className="input-pastel"
                >
                  <option value="LKR">LKR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
          </div>

          {/* Accessibility & Catering */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/60 p-4 rounded-lg space-y-4">
              <h4 className="font-medium text-gray-800 font-poppins">Accessibility Features</h4>
              <div className="space-y-2">
                {accessibilityFeatures.map(feature => (
                  <label key={feature} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.accessibility_features.includes(feature)}
                      onChange={() => handleArrayToggle(formData.accessibility_features, feature, 'accessibility_features')}
                      className="h-4 w-4 bg-white/60 border-pastel-purple-200 rounded text-pastel-purple-600 focus:ring-pastel-purple-400"
                    />
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-white/60 p-4 rounded-lg space-y-4">
              <h4 className="font-medium text-gray-800 font-poppins">Catering Options</h4>
              <div className="space-y-2">
                {cateringOptions.map(option => (
                  <label key={option} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.catering_options.includes(option)}
                      onChange={() => handleArrayToggle(formData.catering_options, option, 'catering_options')}
                      className="h-4 w-4 bg-white/60 border-pastel-purple-200 rounded text-pastel-purple-600 focus:ring-pastel-purple-400"
                    />
                    <span className="text-gray-600 text-sm">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Parking Information */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Parking Information</h4>
            <textarea
              value={formData.parking_info}
              onChange={(e) => setFormData({ ...formData, parking_info: e.target.value })}
              className="input-pastel"
              rows={3}
              placeholder="Describe parking availability, restrictions, and fees..."
            />
          </div>

          {/* Photos */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-800 font-poppins">Photos</h4>
              <div className="flex items-center space-x-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="btn-pastel flex items-center space-x-2 px-3 py-1 text-sm disabled:opacity-50"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span>{uploadingPhoto ? 'Uploading...' : 'Upload Photos'}</span>
                </button>
              </div>
            </div>
            {formData.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo}
                      alt={`Venue photo ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-pastel-purple-200 rounded-lg p-8 text-center cursor-pointer hover:border-pastel-purple-400 hover:bg-white/40 transition-colors"
              >
                <ImageIcon className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Click to upload photos</p>
                <p className="text-gray-400 text-xs mt-1">PNG, JPG up to 5MB each</p>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-pastel-purple-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-pastel px-6 py-2 disabled:opacity-50"
            >
              {loading ? 'Saving...' : venue ? 'Update Venue' : 'Create Venue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
